import { useState } from 'react'
import { Send, CheckCircle } from 'lucide-react'

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    productType: 'insulated',
    quantity: '',
    dimensions: '',
    customization: '',
    message: '',
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // In a real application, you would send this data to your backend
    console.log('Form submitted:', formData)
    setIsSubmitted(true)
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false)
      setFormData({
        name: '',
        email: '',
        company: '',
        productType: 'insulated',
        quantity: '',
        dimensions: '',
        customization: '',
        message: '',
      })
    }, 3000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <section id="quote" className="py-24 bg-apple-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-apple-gray-900 tracking-tight mb-4">
              Get a Free Quote
            </h2>
            <p className="text-lg text-apple-gray-600">
              Fill out the form below and we'll contact you within 24 hours with a customized quote
            </p>
          </div>

          {isSubmitted ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-apple-gray-200">
              <CheckCircle size={64} className="mx-auto mb-6 text-green-500" />
              <h3 className="text-2xl font-semibold text-apple-gray-900 mb-4">
                Thank You!
              </h3>
              <p className="text-apple-gray-600">
                We've received your request and will contact you via email within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-apple-gray-200">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-apple-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-apple-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="company" className="block text-sm font-medium text-apple-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                  placeholder="Your Company Ltd."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="productType" className="block text-sm font-medium text-apple-gray-700 mb-2">
                    Product Type *
                  </label>
                  <select
                    id="productType"
                    name="productType"
                    required
                    value={formData.productType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                  >
                    <option value="insulated">Insulated Bag</option>
                    <option value="gift">Gift Bag</option>
                    <option value="custom">Custom Type</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-apple-gray-700 mb-2">
                    Estimated Quantity *
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    required
                    value={formData.quantity}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                    placeholder="1000"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="dimensions" className="block text-sm font-medium text-apple-gray-700 mb-2">
                  Dimensions (L x W x H in cm)
                </label>
                <input
                  type="text"
                  id="dimensions"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                  placeholder="30 x 20 x 10 cm"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="customization" className="block text-sm font-medium text-apple-gray-700 mb-2">
                  Customization Requirements
                </label>
                <input
                  type="text"
                  id="customization"
                  name="customization"
                  value={formData.customization}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all"
                  placeholder="Logo printing, color preferences, handle type, etc."
                />
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-medium text-apple-gray-700 mb-2">
                  Additional Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-apple-gray-300 rounded-lg focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Any additional details or questions..."
                />
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-apple-gray-900 text-white rounded-lg text-sm font-medium hover:bg-apple-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                Submit Request <Send size={16} />
              </button>

              <p className="text-xs text-apple-gray-500 text-center mt-4">
                By submitting, you agree to our privacy policy. We'll only use your information to respond to your inquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}

export default QuoteForm
